import './index.scss'
import React, { useContext, useState } from 'react'
import EditWriterSidebar from '@Scenes/SongwriterScene/SongwriterMyWritersScene/SongwriterRequestUpdateSidebar/WriterMisspellingForm'
import { StoresContext } from '@App/contexts'
import ConfirmationStep from '@Scenes/SongwriterScene/SongwriterMyWritersScene/SongwriterRequestUpdateSidebar/ConfirmationStep'
import SelectReasonStep, {
    SongwriterUpdateRequestReasonOption,
    UPDATE_REQUEST_REASONS,
} from '@Scenes/SongwriterScene/SongwriterMyWritersScene/SongwriterRequestUpdateSidebar/SelectReasonStep'
import SongwriterIpiUpdateRequestSidebar from '@Scenes/SongwriterScene/SongwriterMyWritersScene/SongwriterIpiUpdateRequestSidebar'
import MergeWriterNamesForm, {
    MergeWriterNamesSelection,
} from '@Scenes/SongwriterScene/SongwriterMyWritersScene/SongwriterRequestUpdateSidebar/MergeWriterNamesForm'
import { SearchResultPartyDetails } from '@Entities/party-details'

export interface SongwriterUpdateRequestFormData {
    correctSpelling: string
    reasonForCorrection: string
}

// Reason value the writer update request endpoint expects for a name correction.
const NAME_UPDATE_REQUEST_REASON = 'Misspelling'

export const MERGE_REQUEST_FAILED_MESSAGE = 'We were unable to submit your merge request. Please try again later.'

interface SongwriterUpdateRequestSidebarProps {
    selectedWriterName: string
    selectedWriterIpId: number
    selectedWriterIpiNumber?: string
    writers?: SearchResultPartyDetails[]
    closeSidebar: () => void
}

const SongwriterRequestUpdateSidebar = (props: SongwriterUpdateRequestSidebarProps) => {
    const { selectedWriterName, selectedWriterIpId, selectedWriterIpiNumber, writers = [], closeSidebar } = props
    const { songwriterWriterSearchStore, popupStore, preloaderStore } = useContext(StoresContext)
    const [reason, setReason] = useState<SongwriterUpdateRequestReasonOption | null>(null)
    const [step, setStep] = useState<'selectReason' | 'form' | 'confirmation'>('selectReason')

    const onBack = () => setStep('selectReason')

    const submitTicket = async (formData: SongwriterUpdateRequestFormData) => {
        preloaderStore.openTransparentPreloader(true)
        try {
            await songwriterWriterSearchStore.submitWriterUpdateRequest(
                selectedWriterIpId,
                selectedWriterName,
                NAME_UPDATE_REQUEST_REASON,
                formData
            )
            setStep('confirmation')
        } catch (error) {
            popupStore.setAlertData((error as Error).message, true)
        } finally {
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const submitMergeRequest = async ({ primaryWriter, writersToMerge }: MergeWriterNamesSelection) => {
        preloaderStore.openTransparentPreloader(true)
        try {
            await songwriterWriterSearchStore.submitWriterMergeRequest(
                Number(primaryWriter.ipId),
                primaryWriter.name,
                writersToMerge.map((writer) => Number(writer.ipId))
            )
            setStep('confirmation')
        } catch {
            popupStore.setAlertData(MERGE_REQUEST_FAILED_MESSAGE, true)
        } finally {
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const renderFormByReason = () => {
        switch (reason?.value) {
            case UPDATE_REQUEST_REASONS.writerName:
                return (
                    <EditWriterSidebar
                        onBack={onBack}
                        onSubmit={submitTicket}
                        selectedWriterName={selectedWriterName}
                    />
                )
            case UPDATE_REQUEST_REASONS.mergeWriters:
                return (
                    <MergeWriterNamesForm
                        writers={writers}
                        initiallySelectedWriterIpId={String(selectedWriterIpId)}
                        onBack={onBack}
                        onSubmit={submitMergeRequest}
                    />
                )
            case UPDATE_REQUEST_REASONS.createIpi:
            case UPDATE_REQUEST_REASONS.updateIpi:
                return (
                    <SongwriterIpiUpdateRequestSidebar
                        selectedWriterIpId={selectedWriterIpId}
                        selectedWriterIpiNumber={selectedWriterIpiNumber}
                        onBack={onBack}
                        closeSidebar={closeSidebar}
                    />
                )
            default:
                return null
        }
    }

    const renderStep = () => {
        switch (step) {
            case 'selectReason':
                return <SelectReasonStep setStep={setStep} reason={reason} setReason={setReason} />
            case 'form':
                return renderFormByReason()
            case 'confirmation':
                return <ConfirmationStep closeSidebar={closeSidebar} />
        }
    }

    return <div className="songwriter-request-update-wrapper">{renderStep()}</div>
}
export default SongwriterRequestUpdateSidebar
